import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { pdf, PDFViewer } from "@react-pdf/renderer";

/**
 * Usage:
 *  <PdfPreviewer>
 *    <LoanTermsSheetPDF formData={...} />
 *  </PdfPreviewer>
 *
 * Notes:
 * - Renders children inside @react-pdf/renderer PDFViewer for an inline preview.
 * - If you pass multiple children, it will render the first valid React element.
 * - Optional props let you control sizing and a loading fallback.
 */
const PdfPreviewer = ({
  children,
  height = "78vh",
  width = "100%",
  sx,
  loadingFallback = null,
}) => {
  const docElement = useMemo(() => {
    const arr = React.Children.toArray(children);
    const firstElement = arr.find((c) => React.isValidElement(c));
    return firstElement || null;
  }, [children]);

  // Lightweight "is it renderable" check:
  // We'll attempt to create a blob when the doc changes.
  // This provides a more reliable loading state than trying to infer render progress from PDFViewer.
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const validate = async () => {
      if (!docElement) return;
      try {
        setLoading(true);
        setError(null);
        // Generates a blob once to validate the tree; PDFViewer will do its own rendering.
        await pdf(docElement).toBlob();
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    validate();

    return () => {
      cancelled = true;
    };
  }, [docElement]);

  if (!docElement) return null;

  return (
    <Box sx={{ height, width, ...(sx || {}) }}>
      {loading && loadingFallback}
      {error ? null : (
        <PDFViewer style={{ width: "100%", height: "100%" }}>
          {docElement}
        </PDFViewer>
      )}
    </Box>
  );
};

export default PdfPreviewer;
